import React, { useEffect, lazy, Suspense } from 'react';
import Terminal from '../components/Terminal';
import './Home.css';

// Lazy load below-the-fold sections to reduce initial JS bundle
const About = lazy(() => import('../components/About'));
const Evolution = lazy(() => import('../components/Evolution'));
const Experience = lazy(() => import('../components/Experience'));
const Contact = lazy(() => import('../components/Contact'));

// Prevent the browser from restoring scroll on its own — we handle it manually
// after lazy sections have rendered with their real heights.
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

const forceRenderSections = () => {
  document.querySelectorAll('.about, .evolution, .experience, .contact').forEach((el) => {
    el.style.contentVisibility = 'visible';
  });
};

const Home = () => {
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('scrollY');
    const shouldScrollToContact = sessionStorage.getItem('scrollToContact');

    // Save scroll position on every unload so we can restore after reload
    const handleBeforeUnload = () => {
      sessionStorage.setItem('scrollY', String(window.scrollY));
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Wait for lazy sections to mount, then restore scroll or navigate to contact.
    // The MutationObserver below fires when they appear in the DOM.
    const pendingRestore = savedScroll && Number(savedScroll) > 0;
    const pendingContact = shouldScrollToContact === 'true';

    if (pendingContact) {
      sessionStorage.removeItem('scrollToContact');
    }
    if (pendingRestore) {
      sessionStorage.removeItem('scrollY');
    }

    const restoreWhenReady = () => {
      // Check if the lazy sections have rendered
      const contact = document.getElementById('contact');
      if (!contact) return false; // not ready yet

      forceRenderSections();

      if (pendingContact) {
        contact.querySelectorAll('.reveal:not(.visible)').forEach((el) => {
          el.classList.add('visible');
        });
        void contact.offsetTop;
        contact.scrollIntoView({ behavior: 'smooth' });
      } else if (pendingRestore) {
        // Force reflow then restore saved position
        void contact.offsetTop;
        window.scrollTo(0, Number(savedScroll));
      }
      return true;
    };

    // Try immediately (sections may already be cached / in DOM)
    const restored = restoreWhenReady();

    // Scroll-reveal via Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );

    // Observe all current .reveal elements
    const observeReveals = () => {
      document.querySelectorAll('.reveal:not(.visible)').forEach((el) => observer.observe(el));
    };
    observeReveals();

    // Watch for lazy-loaded components adding new .reveal elements,
    // and restore scroll position once sections are in the DOM.
    let scrollRestored = restored;
    const mutationObserver = new MutationObserver(() => {
      observeReveals();
      if (!scrollRestored) {
        scrollRestored = restoreWhenReady();
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <div className="home">
      <section className="hero" id="home">
        <div className="container hero-content">
          <Terminal />
        </div>
      </section>

      <Suspense fallback={null}>
        <About />
        <Evolution />
        <Experience />
        <Contact />
      </Suspense>
    </div>
  );
};

export default Home;
